import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, History } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Everything an administrator may correct on a product record. Total Units and
// Rooms stay derived from the matrix and are shown read-only.
const FIELDS = [
  ["product_part_number", "Part Number"],
  ["product_code", "Code"],
  ["product_short", "Short Code"],
  ["product_model", "Model"],
  ["product_keynote", "Keynote"],
  ["manufacturer", "Manufacturer"],
  ["product_color", "Color"],
  ["unit_of_measure", "Unit of measure"],
  ["product_desc", "Description", "wide"],
  ["product_description", "Full FRS Text", "wide"],
];
const LABEL = { ...Object.fromEntries(FIELDS.map(([f, l]) => [f, l])), scope_type: "Scope" };

export const ProductRecordDialog = ({ projectId, product, scopes = [], role, actor, onClose, onSaved }) => {
  const scopeOf = product.scope_type || "";
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(([f]) => [f, product[f] || ""])),
    scope_type: scopeOf,
  }));
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(null);     // { error, message }
  const [busy, setBusy] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/frs-editor/product-records/history", {
        params: { project_id: projectId, product_uid: product.product_uid },
      });
      setHistory(data || []);
    } catch { setHistory([]); }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [product.product_uid]);

  const dirty = Object.entries(form).filter(([f, v]) => (v || "") !== (f === "scope_type" ? scopeOf : (product[f] || "")));

  const save = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/frs-editor/product-records", {
        project_id: projectId, scope_type: scopeOf, product_uid: product.product_uid,
        fields: Object.fromEntries(dirty), role, actor, force,
      });
      toast.success(data.changes.length
        ? `${data.changes.length} field(s) updated across ${data.rows_updated} FRS row(s)`
        : "Nothing to change");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "new_scope" || d?.error === "product_exists") setConfirm(d);
      else toast.error(typeof d === "string" ? d : d?.message || "Could not save that product");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="product-record-dialog">
        <DialogHeader>
          <DialogTitle>Edit product — {product.label || product.product_code}</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Saving rewrites every FRS row on this job that carries this product, so the RA grid,
            the installer view, the pull ticket and the delivery view all update at once.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Scope</Label>
            <select value={form.scope_type} data-testid="product-record-scope_type"
                    onChange={(e) => setForm({ ...form, scope_type: e.target.value })}
                    className="h-8 w-full text-xs border border-gray-300 rounded px-1 bg-white">
              {[...new Set([scopeOf, ...scopes].filter(Boolean))].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {FIELDS.map(([f, label, wide]) => (
            <div key={f} className={wide ? "col-span-2" : ""}>
              <Label className="text-[10px] uppercase text-gray-500">{label}</Label>
              <Input value={form[f]} className="h-8 text-xs"
                     data-testid={`product-record-${f}`}
                     onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
            </div>
          ))}
          <div className="col-span-2 flex items-center gap-4 text-xs text-gray-500 border-t border-gray-100 pt-2">
            <span data-testid="product-record-units">Total units <span className="font-semibold text-gray-800 tabular-nums">{product.units || 0}</span></span>
            <span data-testid="product-record-rooms">Rooms <span className="font-semibold text-gray-800 tabular-nums">{product.rooms || 0}</span></span>
            <span className="text-gray-400">Derived from the matrix — set them with Set quantities.</span>
          </div>
        </div>

        {confirm && (
          <div className="border border-amber-300 bg-amber-50 rounded p-2 text-xs space-y-2"
               data-testid="product-record-confirm">
            <div className="text-amber-900 flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{confirm.message}</span>
            </div>
            <Button size="sm" className="h-7" disabled={busy} onClick={() => { setConfirm(null); save(true); }}
                    data-testid="product-record-confirm-save">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Yes, continue
            </Button>
          </div>
        )}

        <div className="border border-gray-200 rounded" data-testid="product-record-history">
          <div className="px-2 py-1 bg-gray-50 border-b border-gray-200 text-[10px] uppercase text-gray-500 flex items-center gap-1">
            <History className="w-3 h-3" /> Change history
          </div>
          {history.length === 0 ? (
            <div className="p-2 text-[11px] text-gray-400" data-testid="product-record-history-empty">
              No product data changes yet.
            </div>
          ) : (
            <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
              {history.map((h) => (
                <div key={h.id} className="px-2 py-1 text-[11px]" data-testid={`product-record-history-${h.id}`}>
                  <span className="font-medium text-gray-800">{LABEL[h.field] || h.field}</span>
                  <span className="text-gray-400">{": "}</span>
                  <span className="text-gray-500 line-through">{h.old_value || "—"}</span>
                  <span className="text-gray-400">{" → "}</span>
                  <span className="text-gray-900">{h.new_value || "—"}</span>
                  <span className="text-gray-400">{` · ${h.actor || h.role || "unknown"} · ${String(h.at || "").slice(0, 16).replace("T", " ")}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} data-testid="product-record-cancel">Cancel</Button>
          <Button disabled={busy || dirty.length === 0} onClick={() => save(false)}
                  data-testid="product-record-save">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Save {dirty.length > 0 ? `(${dirty.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductRecordDialog;
